import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { PitchCardModalBase } from '../pitchcard-modal-base';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ElementRef,
  HostListener,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { PhonePipe } from '@App/modules/pitch-card-shared/pipes';
import { BusinessService, UserCommonApiService } from '@App/common/services';
import {
  CommonBindingDataService,
  NotificationService
} from '@Shared/services';
import { LoaderService } from '@Shared/components/loader/loader.service';
import {
  PitchCardModalsWrapperService,
  PitchCardService
} from '@App/modules/pitch-card-shared/services';
import { Clipboard } from '@angular/cdk/clipboard';
import { video_extensions } from '@Shared/mock/file-types.mock';
import { AppSettings } from '@Shared/app.settings';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-pitchcard-learn-about-me-modal',
  standalone: true,

  imports: [
    CommonModule,
    DialogModule,
    TranslateModule,
    NgxDocViewerModule,
    PhonePipe
  ],
  templateUrl: './pitchcard-learn-about-me-modal.component.html'
})
export class PitchCardLearnAboutMeModalComponent extends PitchCardModalBase {
  @Input() showLearnAboutMeDialog: any;
  @Input() currentPitchCard: any;
  @Input() learnAboutMeReviews: any;
  @Input() isMobile: any;
  @Input() isSearch: any;
  @Input() googleMapLink: any;
  @Input() scaleFactor: any;
  photoGalleriaImages: any;
  private destroyRef = inject(DestroyRef);
  @Output() playVideo = new EventEmitter<string>();
  @Output() reviewsCleared = new EventEmitter<void>();
  @Output() uploadReset = new EventEmitter<void>();
  @Output() fileSelected = new EventEmitter<File>();
  @Output() requestContactSwap = new EventEmitter<void>();

  readonly AppSettings = AppSettings;
  currentContactMenu: any;
  isContactMenuOpen = false;
  isMouseOverContactMenu = false;
  @ViewChild('videoFileInput', { static: false }) videoFileInput: ElementRef;
  private businessService = inject(BusinessService);
  private userCommonService = inject(UserCommonApiService);
  private commonBindingService = inject(CommonBindingDataService);
  private notificationService = inject(NotificationService);
  private loaderService = inject(LoaderService);
  private pitchCardModalsWrapperService = inject(PitchCardModalsWrapperService);
  private pitchCardService = inject(PitchCardService);
  private clipboard = inject(Clipboard);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  closeContactMenu() {
    if (this.currentContactMenu) {
      this.currentContactMenu.classList.remove('block');
      this.isContactMenuOpen = false;
    }
  }

  @HostListener('focusout')
  onBlur() {
    if (this.isContactMenuOpen && !this.isMouseOverContactMenu) {
      this.currentContactMenu?.classList?.remove('block');
      this.isContactMenuOpen = !this.isContactMenuOpen;
    }
  }

  addCallsAnalytics() {
    if (isPlatformBrowser(this.platformId)) {
      window.location.href = `tel:${this.currentPitchCard.contactNumber}`;
    }
    this.businessService
      .contactCall([this.currentPitchCard.id])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {});
  }

  addEmailAnalytics() {
    if (isPlatformBrowser(this.platformId)) {
      window.location.href = `mailto:${this.currentPitchCard.email}`;
    }
    this.businessService
      .contactEmail([this.currentPitchCard.id])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {});
  }

  addLocationAnalytics() {
    this.businessService
      .contactDirections([this.currentPitchCard.id])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {});
  }

  addTextAnalytics() {
    if (!this.isMobile) {
      this.clipboard.copy(this.currentPitchCard.textingNumber);
      this.notificationService.showNotification(
        'SUCCESS',
        'Mobile number copied to clipboard'
      );
    } else {
      if (isPlatformBrowser(this.platformId)) {
        window.location.href = `sms:${this.currentPitchCard.textingNumber};?body=`;
      }
    }
  }

  getFilePreviewSrc(url: string) {
    if (url?.includes('.doc') || url?.includes('.docx')) {
      return '/assets/images/fa-icons/word-file.svg';
    } else if (url?.includes('.pdf')) {
      return '/assets/images/fa-icons/pdf-file.svg';
    } else {
      return url;
    }
  }

  getTextColor(primaryPitchColor: any) {
    return this.pitchCardService.getTextColor(primaryPitchColor);
  }

  hideLearnAboutMeDialog() {
    this.onFlag('showLearnAboutMeDialog', false);
    this.reviewsCleared.emit();
    this.uploadReset.emit();
    this.cdr.detectChanges();
  }

  isDocument(fileUrl: string) {
    if (fileUrl) {
      return (
        fileUrl.includes('.pdf') ||
        fileUrl.includes('.doc') ||
        fileUrl.includes('.docx')
      );
    } else {
      return false;
    }
  }

  mouseEnterContactMenu() {
    this.isMouseOverContactMenu = true;
  }

  mouseLeaveContactMenu() {
    this.isMouseOverContactMenu = false;
  }

  onTitleClick(title: string) {
    if (this.currentPitchCard) {
      this.pitchCardModalsWrapperService.onTitleClick(title);
    }
  }

  openGallery(index: number, isOpening: boolean) {
    this.photoGalleriaImages = this.currentPitchCard.employeePictureThumnailUrl;
    this.pitchCardModalsWrapperService.showPhotoGalleria.next({
      index,
      isOpening
    });
  }

  showCalendar(calendarLink: string) {
    if (isPlatformBrowser(this.platformId)) {
      window.open(calendarLink, '_blank');
    }
  }

  toggleContactMenu(e: Event, parent = null) {
    e.stopPropagation();
    if (!this.isContactMenuOpen && parent) {
      this.currentContactMenu = parent;
      this.currentContactMenu.classList.add('block');
      this.isContactMenuOpen = !this.isContactMenuOpen;
      this.currentContactMenu.style.bottom =
        -1 * parent.firstChild.clientHeight + 'px';
    } else {
      this.currentContactMenu.classList.remove('block');
      this.isContactMenuOpen = !this.isContactMenuOpen;
    }
    this.currentContactMenu = parent;
  }

  videoFileChangeEvent(event: any) {
    this.loaderService.show();
    Array.from(event.target.files).map((file: any) => {
      const extension = file.name.split('.').pop();
      if (
        video_extensions.some(
          (ext) => ext.toLowerCase() === '.' + extension.toLowerCase()
        )
      ) {
        const maxFileSize = 500 * 1024 * 1024;
        if (file.size > maxFileSize) {
          this.loaderService.hide();
          this.notificationService.showNotification(
            'ERROR',
            'File size should be less than 500MB, your file size is ' +
              (file.size / (1024 * 1024)).toFixed(1) +
              'MB'
          );
          return;
        }

        if (isPlatformBrowser(this.platformId)) {
          const video = document.createElement('video');
          let duration = 0;
          video.preload = 'metadata';
          video.onloadedmetadata = () => {
            duration = video.duration;
            this.fileSelected.emit(file);
            video.remove();
          };

          video.src = URL.createObjectURL(file);
        }
      } else {
        this.notificationService.showNotification(
          'ERROR',
          'This format is not supported!'
        );
        this.loaderService.hide();
      }
    });
    this.videoFileInput.nativeElement.value = '';
  }
}
